import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DragHandleOutlinedIcon from "@mui/icons-material/DragHandleOutlined";
// import Paper from "@mui/material/Paper";
import {
  Avatar,
  Checkbox,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import DataStatusAlert from "../../../Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import {
  setPage,
  setRowsPerPage,
  setSelectedRows,
  setSingleRowData,
  setSort,
} from "../../../../../features/actionItemsSlice";
import ActionItemFilterCard from "../ActionItemFilterCard/ActionItemFilterCard";
import { setOpenModalType } from "../../../../../features/actionItemsSlice";
import SnoozePopup from "../ActionItemsTableActions/SnoozeAction/SnoozePopup";
import AssignPopup from "../ActionItemsTableActions/AssignAction/AssignPopup";
import ResolveAction from "../ActionItemsTableActions/ResolveAction/ResolveAction";
import { selectUserData } from "../../../../../features/userSlice";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { capitalizeEachWords, formatDate } from "../../../../../utils/helpers";

const ActioItemsTable = () => {
  const {
    actionItemsList,
    selectedActionItem,
    selectedCategory,
    selectedAssignee,
    selectedStatus,
    selectedDueDate,
    selectedPriority,
    selectedTarget,
    createdOnDate,
    sort,
    page,
    rowsPerPage,
    selectedRows,
    openModalType,
  } = useSelector((state: any) => state?.actionItems);
  const [sortedData, setSortedData] = React.useState<any[]>([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  //-- Get User Permissions for Action Items
  const response = useSelector(selectUserData);
  const hasEditActionItemsPermission = CheckPermissions(
    response?.data?.products?.assets,
    "overview",
    "edit_action_items"
  );

  const filteredData = sortedData.filter((actionItems: any) => {
    const selectedActionItemsName =
      selectedActionItem && selectedActionItem.length !== 0
        ? selectedActionItem.includes(actionItems?.data["action_item"])
        : true;

    const selectedCategoryName =
      selectedCategory && selectedCategory.length !== 0
        ? selectedCategory.includes(actionItems?.data["category"])
        : true;

    const selectedStatusName =
      selectedStatus && selectedStatus.length !== 0
        ? selectedStatus.includes(actionItems?.current_status)
        : true;

    const selectedPriorityName =
      selectedPriority && selectedPriority.length !== 0
        ? selectedPriority.includes(actionItems?.data["priority"])
        : true;

    const selectedAssigneeName =
      selectedAssignee && selectedAssignee.length !== 0
        ? selectedAssignee.includes(actionItems?.assignee_fullname)
        : true;

    const selectedTargetName =
      selectedTarget && selectedTarget.length !== 0
        ? selectedTarget.includes(actionItems?.data["target"])
        : true;

    const selectedDueDateRange =
      selectedDueDate &&
      selectedDueDate.length === 2 &&
      selectedDueDate[0] &&
      selectedDueDate[1]
        ? selectedDueDate[0] === selectedDueDate[1] // If both dates are the same, check for equality
          ? new Date(actionItems?.due_date).toDateString() ===
            new Date(selectedDueDate[0]).toDateString()
          : new Date(actionItems?.due_date) >= new Date(selectedDueDate[0]) &&
            new Date(actionItems?.due_date) <=
              new Date(new Date(selectedDueDate[1]).setHours(23, 59, 59, 999))
        : true;

    const selectedDateRange =
      createdOnDate &&
      createdOnDate.length === 2 &&
      createdOnDate[0] &&
      createdOnDate[1]
        ? createdOnDate[0] === createdOnDate[1] // If both dates are the same, check for equality
          ? new Date(actionItems?.modified_at).toDateString() ===
            new Date(createdOnDate[0]).toDateString()
          : new Date(actionItems?.modified_at) >= new Date(createdOnDate[0]) &&
            new Date(actionItems?.modified_at) <=
              new Date(new Date(createdOnDate[1]).setHours(23, 59, 59, 999))
        : true;

    return (
      selectedActionItemsName &&
      selectedCategoryName &&
      selectedStatusName &&
      selectedPriorityName &&
      selectedAssigneeName &&
      selectedTargetName &&
      selectedDueDateRange &&
      selectedDateRange
    );
  });

  //-- Sorting Data
  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        // Default sorting by modified_at (To make new items appear first in your table when they are Created or Added)
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        let aValue, bValue;

        // Handle nested keys (e.g., "Category" or "Target")
        if (keytoSort === "action_item") {
          aValue = a.data["action_item"];
          bValue = b.data["action_item"];
        } else if (keytoSort === "category") {
          aValue = a.data["category"];
          bValue = b.data["category"];
        } else if (keytoSort === "target") {
          aValue = a.data["target"];
          bValue = b.data["target"];
        }
        // Handle "Status Action Date" logic
        else if (keytoSort === "status_action_date") {
          if (a.current_status === "Snooze") {
            aValue = a.snooze_end;
          } else if (a.current_status === "In Progress") {
            aValue = a.status_log[0].modified_at;
          } else {
            aValue = a.status_log[0].modified_at;
          }

          if (b.current_status === "Snooze") {
            bValue = b.snooze_end;
          } else if (b.current_status === "In Progress") {
            bValue = b.status_log[0].modified_at;
          } else {
            bValue = b.status_log[0].modified_at;
          }
        }
        // Default case for other columns
        else {
          aValue = a[keytoSort];
          bValue = b[keytoSort];
        }

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  // headers for the table columns with sortable option for each column
  const tableheaders = [
    { id: 1, KEY: "action_item", label: "Item Name", sortable: true },
    {
      id: 2,
      KEY: "category", // Use the nested key
      label: "Category",
      sortable: true,
    },
    { id: 3, KEY: "current_status", label: "Status", sortable: true },
    {
      id: 4,
      KEY: "status_action_date", // Use the custom logic key
      label: "Status Action Date",
      sortable: true,
    },
    { id: 5, KEY: "due_date", label: "Due Date", sortable: true },
    { id: 6, KEY: "assignee_fullname", label: "Assignee", sortable: true },
    { id: 7, KEY: "owner_fullname", label: "Owner", sortable: true },
    { id: 8, KEY: "modified_at", label: "Created On", sortable: true },
    { id: 9, KEY: "target", label: "Target", sortable: true },
  ];

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    dispatch(
      setSort({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  // -- Function to handle individual row selection using checkbox
  const handleSelectRow = (row: any) => {
    const updatedSelectedRows = selectedRows.includes(row)
      ? selectedRows.filter((i: any) => i !== row) // Remove if already selected
      : [...selectedRows, row]; // Add if not selected
    dispatch(setSelectedRows(updatedSelectedRows));
  };

  // -- Function to handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));
    const updatedParams = new URLSearchParams(window.location.search);
    updatedParams.set("page", newPage.toString());
    setSearchParams(updatedParams);
  };

  //-- Function to handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };

  //-- Function to open modal(snooze/assign/resolve)
  const handleModalOpen = (modalType: string) => {
    dispatch(setOpenModalType(modalType));
  };

  //-- Function to close modal(snooze/assign/resolve)
  const handleCloseModal = () => {
    dispatch(setOpenModalType(null));
  };

  //-- Set Sorted Data
  useEffect(() => {
    const sortedRows = getSortedData(actionItemsList || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [actionItemsList, getSortedData]);

  useEffect(() => {
    dispatch(setPage(0));
    // eslint-disable-next-line
  }, [
    selectedActionItem,
    selectedCategory,
    selectedStatus,
    selectedPriority,
    selectedAssignee,
    selectedTarget,
    selectedDueDate,
    createdOnDate,
  ]);

  useEffect(() => {
    dispatch(setSelectedRows([]));
    dispatch(setPage(Number(searchParams.get("page")) || 0));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
    // eslint-disable-next-line
  }, [searchParams]);

  return (
    <>
      <ActionItemFilterCard
        onSnoozeIconClick={() => handleModalOpen("snooze")}
        onAssignIconClick={() => handleModalOpen("assign")}
        onResolveIconClick={() => handleModalOpen("resolve")}
      />
      <TableContainer className="PageScantablewrap">
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length > 0 ? (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any, index: any) => (
                  <TableRow key={index} selected={selectedRows.includes(index)}>
                    <TableCell align="left">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Typography
                          variant="body1"
                          component="span"
                          fontSize={14}
                        >
                          <Stack
                            alignItems="center"
                            gap={1}
                            flexDirection="row"
                          >
                            <Checkbox
                              checked={selectedRows.includes(row)}
                              color="secondary"
                              onChange={() => handleSelectRow(row)}
                              sx={{ paddingLeft: 0, marginLeft: 0 }}
                            />

                            {row?.data["priority"]?.toLowerCase() === "high" ? (
                              <KeyboardArrowUpIcon sx={{ color: "#D32F2F" }} />
                            ) : row?.data["priority"]?.toLowerCase() ===
                              "low" ? (
                              <KeyboardArrowDownIcon
                                sx={{ color: "#2196F3" }}
                              />
                            ) : row?.data["priority"]?.toLowerCase() ===
                              "lowest" ? (
                              <KeyboardDoubleArrowDownIcon
                                sx={{ color: "#2196F3" }}
                              />
                            ) : row?.data["priority"]?.toLowerCase() ===
                              "highest" ? (
                              <KeyboardDoubleArrowUpIcon
                                sx={{ color: "#D32F2F" }}
                              />
                            ) : row?.data["priority"]?.toLowerCase() ===
                              "medium" ? (
                              <DragHandleOutlinedIcon
                                sx={{ color: "#ED6C02" }}
                              />
                            ) : null}

                            {hasEditActionItemsPermission ? (
                              <RouterLink
                                data-tracker="110"
                                to="#"
                                style={{
                                  textDecoration: "underline",
                                  color: "#9C27B0",
                                }}
                                onClick={(event) => {
                                  event.preventDefault(); // stop default fetures
                                  dispatch(setSingleRowData(row));
                                  const params = new URLSearchParams(
                                    location.search
                                  );
                                  params.set(
                                    "actionItemSharedUid",
                                    row?.shared_uid
                                  ); // Add new param while keeping others
                                  navigate(
                                    `edit_action_item?${params.toString()}`
                                  );
                                }}
                              >
                                {row?.data["action_item"] &&
                                  row.data["action_item"]
                                    .charAt(0)
                                    .toUpperCase() +
                                    row?.data["action_item"].slice(1)}
                              </RouterLink>
                            ) : (
                              row?.data["action_item"] &&
                              row.data["action_item"].charAt(0).toUpperCase() +
                                row?.data["action_item"].slice(1)
                            )}
                          </Stack>
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell align="left">{row?.data["category"]}</TableCell>
                    <TableCell align="left">
                      <DataStatusAlert status={row?.current_status} />
                    </TableCell>
                    <TableCell align="left">
                      {(() => {
                        const lastModified = row?.status_log.reduce(
                          (latest: any, entry: any) =>
                            new Date(entry.modified_at) >
                            new Date(latest.modified_at)
                              ? entry
                              : latest
                        );
                        if (row?.current_status === "Snooze") {
                          return `Until ${dayjs(row?.snooze_end).format(
                            "DD/MM/YYYY"
                          )}`;
                        } else if (row?.current_status === "In Progress") {
                          return `Since ${dayjs(
                            lastModified?.modified_at
                          ).format("DD/MM/YYYY")}`;
                        } else {
                          return `On ${dayjs(lastModified?.modified_at).format(
                            "DD/MM/YYYY"
                          )}`;
                        }
                      })()}
                    </TableCell>
                    <TableCell align="left">
                      {row?.due_date ? formatDate(row?.due_date) : ""}
                    </TableCell>
                    <TableCell align="left">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 22,
                            height: 22,
                            fontSize: 12,
                          }}
                        >
                          {row?.assignee_fullname?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        {row?.assignee_fullname
                          ? capitalizeEachWords(row?.assignee_fullname)
                          : ""}
                      </div>
                    </TableCell>
                    <TableCell align="left">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 22,
                            height: 22,
                            fontSize: 12,
                          }}
                        >
                          {row?.owner_fullname?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        {row?.owner_fullname
                          ? capitalizeEachWords(row?.owner_fullname)
                          : ""}
                      </div>
                    </TableCell>
                    <TableCell align="left">
                      {row?.modified_at ? formatDate(row?.modified_at): ""}
                    </TableCell>
                    <TableCell align="left">
                      {row?.data["target"]
                        ? capitalizeEachWords(row?.data["target"])
                        : "N/A"}
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No Data Available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={filteredData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(filteredData?.length / rowsPerPage)}
            page={page + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_, value) => handleChangePage(null, value - 1)}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={filteredData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
      </TableContainer>

      {/* This For Modal */}
      {openModalType === "assign" && (
        <AssignPopup open={true} handleClose={handleCloseModal} />
      )}
      {openModalType === "snooze" && (
        <SnoozePopup open={true} handleClose={handleCloseModal} />
      )}
      {openModalType === "resolve" && (
        <ResolveAction open={true} handleClose={handleCloseModal} />
      )}
    </>
  );
};

export default ActioItemsTable;
